'use client'

import dynamic from 'next/dynamic'
import { useEffect, useMemo } from 'react'
import classNames from 'classnames'
import { useIntersectionObserver, useLayout } from '../hooks'

const BannerflowBannerParser = dynamic(() =>
  import('./bannerflow-banner-parser').then(mod => mod.BannerflowBannerParser)
)

type ScriptContent = {
  script?: string | null
  scriptMobile?: string | null
}
type ImageTagContent = {
  dynamicImage?: string
  dynamicImageMobile?: string
}

type BannerContent = {
  properties: ScriptContent & ImageTagContent
  type: string
}

type Props = ScriptContent & {
  bordered?: boolean
  content?: Array<BannerContent>
}

function parseScriptSrc(script: string): string {
  if (script.startsWith('<script')) {
    return script.match(/src="([^"]*)"/i)?.[1] || ''
  } else {
    return script
  }
}

export const BannerflowBanner = ({
  script: upLevelScript,
  scriptMobile: upLevelScriptMobile,
  content,
  bordered,
}: Props) => {
  const { ref: containerRef, isVisible } = useIntersectionObserver({})
  const { responsive } = useLayout()

  const {
    dynamicImage: dynamicImageDesktop,
    scriptMobile: mobileScript,
    dynamicImageMobile,
    script,
  } = content?.[0]?.properties || {}
  const scriptDesktop = upLevelScript || script
  const scriptMobile = upLevelScriptMobile || mobileScript
  const isMobile = responsive.breakpoints.max.SM
  const reloadKey = `${responsive.breakpoints.min.SM}`

  useEffect(() => {
    if (scriptDesktop && scriptMobile) {
      const triggerClick = (target: HTMLDivElement) => {
        const currentIframe = target?.querySelector('iframe')
        const dataBannerTarget = currentIframe?.contentDocument?.body.querySelector(
          '[data-banner]'
        ) as HTMLDivElement

        dataBannerTarget?.click()
      }

      const clickEvent = ({ target }: MouseEvent) => {
        if (!target) {
          return
        }

        triggerClick(target as HTMLDivElement)
      }

      const keyPressEvent = ({ key, target }: KeyboardEvent) => {
        if (!target || key !== 'Enter') {
          return
        }

        triggerClick(target as HTMLDivElement)
      }

      const container = containerRef.current

      container?.addEventListener('click', clickEvent)
      container?.addEventListener('keypress', keyPressEvent)

      return () => {
        container?.removeEventListener('click', clickEvent)
        container?.removeEventListener('keypress', keyPressEvent)
      }
    }
  }, [containerRef, scriptDesktop, scriptMobile])

  const getBanner = (
    script: string | undefined | null,
    dynamicImage: string | undefined,
    reloadKey: string
  ) => {
    return dynamicImage ? (
      <div dangerouslySetInnerHTML={{ __html: dynamicImage }} />
    ) : (
      script && <BannerflowBannerParser async src={parseScriptSrc(script)} key={reloadKey} />
    )
  }

  const bannerScript = isMobile ? scriptMobile : scriptDesktop
  const bannerDynamicImage = isMobile ? dynamicImageMobile : dynamicImageDesktop

  const banner = useMemo(
    () => isVisible && getBanner(bannerScript, bannerDynamicImage, reloadKey),
    [bannerDynamicImage, bannerScript, isVisible, reloadKey]
  )

  return (
    <div
      className={classNames('c-bannerflowbanner', {
        'c-bannerflowbanner--bordered': bordered,
      })}>
      <div
        className={classNames('c-bannerflowbanner__wrapper', {
          'c-bannerflowbanner--mobile': isMobile,
          'c-bannerflowbanner--desktop': !isMobile,
        })}>
        <div className="c-bannerflowbanner__container" ref={containerRef}>
          {banner ?? null}
        </div>
      </div>
    </div>
  )
}
